import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  CommunicationStatus,
  CommunicationType,
  DocumentSubType,
  type FormInt,
  ObjectTypeEnum,
  type ViewCommunication,
  type ViewCommunicationItem,
} from "@/briefpoint-client";
import type { QueuedOperation } from "@/components/CollectFromClientModal/utils";
import {
  useCaseApi,
  useCommunicationApi,
  useDocumentApi,
  useObjectionApi,
} from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import { SEARCH_PARAMS } from "@/lib/constants";
import {
  caseGetOptions,
  getCommunicationsForObjectOptions,
  getDocumentOptions,
  getFormInterrogatoriesOptions,
} from "@/lib/query-options";
import type { DocumentDetailClient } from "@/lib/types";
import { getInterrogatoryDisplayNumber, getSubTypeText } from "@/lib/utils";

/**
 * central state for requesting and tracking client input on the current document step:
 * reads route params + `collectFromClient` search param, loads the document, case, communications,
 * and the interrogatory for the active step; exposes the active client communication, modal open
 * handler, and helpers used by CollectFromClientModal, client tasks, uploads, and related UI.
 */
export function useCollectFromClient() {
  const location = useLocation();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { documentId, currentStep } = useParams<{
    documentId: string;
    currentStep: string;
  }>();
  const documentApi = useDocumentApi();
  const objectionApi = useObjectionApi();
  const communicationApi = useCommunicationApi();
  const caseApi = useCaseApi();

  // Determine if modal should be shown based on URL param
  const searchParams = new URLSearchParams(location.search);
  const show = searchParams.get(SEARCH_PARAMS.COLLECT_FROM_CLIENT) === "true";

  const stepNumber = Number(currentStep);

  /* QUERIES */
  const documentQuery = useQuery({
    ...getDocumentOptions(documentId, documentApi),
    enabled: !!documentId,
  });

  const caseQuery = useQuery({
    ...caseGetOptions(
      caseApi,
      user?.firmId,
      documentQuery.data?.caseId ?? undefined,
    ),
    enabled: !!user?.firmId && !!documentQuery.data?.caseId && show,
  });

  const formInterrogatoriesQuery = useQuery({
    ...getFormInterrogatoriesOptions(objectionApi),
    enabled:
      show &&
      documentQuery.data?.info?.documentSubType ===
        DocumentSubType.FormInterrogatories,
  });

  const communicationsQuery = useQuery({
    ...getCommunicationsForObjectOptions(
      communicationApi,
      documentId,
      ObjectTypeEnum.Document,
    ),
    select: (data) => {
      return data.filter(
        (c) =>
          c.status !== CommunicationStatus.Closed &&
          c.status !== CommunicationStatus.Deleted,
      );
    },
    enabled: !!documentId,
  });

  /* OPERATION QUEUE SYSTEM */
  // Queue for batching associate/disassociate operations
  const operationQueueRef = useRef<QueuedOperation[]>([]);
  // Ref for synchronous guard - prevents race conditions when processQueue is called rapidly
  const isProcessingQueueRef = useRef(false);
  // State for UI reactivity - triggers re-renders to show loading states, disable buttons, etc.
  const [isProcessingQueue, setIsProcessingQueue] = useState(false);

  const communicationsQueryKey = getCommunicationsForObjectOptions(
    communicationApi,
    documentId,
    ObjectTypeEnum.Document,
  ).queryKey;

  const invalidateCommunications = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: communicationsQueryKey });
  }, [queryClient, communicationsQueryKey]);

  /* DERIVED STATE */
  const interrogatory = useMemo(() => {
    if (!documentQuery.data?.interrogatories) return null;
    return documentQuery.data.interrogatories[stepNumber] ?? null;
  }, [documentQuery.data, stepNumber]);

  // activeCommunication is the first communication for the document with no requestedReviewIds (i.e., the active client review communication), or null if none.
  // If no draft communication exists, fall back to a sent/open communication that has tasks for this interrogatory.
  const activeCommunication = useMemo((): ViewCommunication | null => {
    if (!communicationsQuery.data || !interrogatory?.id) return null;

    const currentInterrogatoryId = interrogatory.id.toString();

    // First, try to find a draft communication (no requestedReviewIds) - this is editable
    const draftComm = communicationsQuery.data.find(
      (c: ViewCommunication) =>
        !c.communicationInfo?.requestedReviewIds ||
        c.communicationInfo.requestedReviewIds.length === 0,
    );

    if (draftComm) return draftComm;

    // If no draft exists, find a sent/open/in-progress communication that has tasks for this interrogatory
    const sentComm = communicationsQuery.data.find((c: ViewCommunication) => {
      // Check if this communication has tasks associated with the current interrogatory
      const hasTasksForInterrogatory = c.externalCommunicationItems?.some(
        (item) =>
          item.responseInfo?.associatedObjectIds?.includes(
            currentInterrogatoryId,
          ) && !item.parentCommunicationId,
      );

      return hasTasksForInterrogatory;
    });

    return sentComm ?? null;
  }, [communicationsQuery.data, interrogatory]);

  // Check if ANY communication has been sent (not just the active one for this interrogatory)
  const hasAnySentCommunication = useMemo(() => {
    if (!communicationsQuery.data) return false;

    return communicationsQuery.data.some((c: ViewCommunication) => {
      // Check if communication has been sent
      const hasRequestedReviews =
        c.communicationInfo?.requestedReviewIds &&
        c.communicationInfo.requestedReviewIds.length > 0;

      const hasSentStatus =
        c.status === CommunicationStatus.Sent ||
        c.status === CommunicationStatus.Open ||
        c.status === CommunicationStatus.InProgress ||
        c.status === CommunicationStatus.ChangesRequested ||
        c.status === CommunicationStatus.Complete;

      return hasRequestedReviews || hasSentStatus;
    });
  }, [communicationsQuery.data]);

  const formInterrogatory = useMemo(() => {
    if (
      documentQuery.data?.info?.documentSubType !==
      DocumentSubType.FormInterrogatories
    ) {
      return null;
    }
    if (!formInterrogatoriesQuery.data || !interrogatory?.text) return null;

    return (
      formInterrogatoriesQuery.data.find(
        (fi: FormInt) => fi.id === interrogatory.text,
      ) ?? null
    );
  }, [formInterrogatoriesQuery.data, interrogatory, documentQuery.data]);

  const interrogatoryText = useMemo(() => {
    if (formInterrogatory?.text) return formInterrogatory.text;
    return interrogatory?.text ?? "";
  }, [interrogatory, formInterrogatory]);

  const doc = documentQuery.data;

  const number = useMemo(() => {
    if (!doc || !interrogatory) return "";
    return getInterrogatoryDisplayNumber(
      doc as DocumentDetailClient,
      interrogatory,
      formInterrogatory,
    );
  }, [doc, interrogatory, formInterrogatory]);

  const requestName = useMemo(() => {
    if (!doc?.info) return "Request";
    return getSubTypeText(doc.info.documentSubType, doc.info.jurisdiction ?? "")
      .request;
  }, [doc]);

  const isLoading =
    documentQuery.isLoading ||
    communicationsQuery.isLoading ||
    caseQuery.isLoading ||
    (documentQuery.data?.info?.documentSubType ===
      DocumentSubType.FormInterrogatories &&
      formInterrogatoriesQuery.isLoading);

  // Sequential queue processor - processes operations one at a time
  const processQueue = useCallback(async () => {
    if (
      isProcessingQueueRef.current ||
      operationQueueRef.current.length === 0
    ) {
      return;
    }

    isProcessingQueueRef.current = true;
    setIsProcessingQueue(true);

    while (operationQueueRef.current.length > 0) {
      const operation = operationQueueRef.current.shift();
      if (!operation || !interrogatory?.id) continue;

      const currentInterrogatoryId = interrogatory.id.toString();

      try {
        if (operation.type === "associate") {
          const task = operation.task;
          const existingAssociatedIds =
            task.responseInfo?.associatedObjectIds ?? [];

          await communicationApi.communicationPutCommunicationItem({
            id: task.id ?? "",
            putCommunicationItem: {
              id: task.id,
              objectType: task.objectType,
              objectId: task.objectId,
              communication: task.communication,
              language: task.language,
              responseInfo: {
                ...task.responseInfo,
                associatedObjectIds: [
                  ...existingAssociatedIds,
                  currentInterrogatoryId,
                ],
              },
            },
          });
        } else if (operation.type === "disassociate") {
          const task = operation.task;
          const existingAssociatedIds =
            task.responseInfo?.associatedObjectIds ?? [];
          const newAssociatedIds = existingAssociatedIds.filter(
            (id: string) => id !== currentInterrogatoryId,
          );

          await communicationApi.communicationPutCommunicationItem({
            id: task.id ?? "",
            putCommunicationItem: {
              id: task.id,
              objectType: task.objectType,
              objectId: task.objectId,
              communication: task.communication,
              language: task.language,
              responseInfo: {
                ...task.responseInfo,
                associatedObjectIds: newAssociatedIds,
              },
            },
          });
        }
      } catch (error) {
        console.error(`Failed to ${operation.type} task:`, error);
      }
    }

    isProcessingQueueRef.current = false;
    setIsProcessingQueue(false);
    // Refresh data after all operations complete
    invalidateCommunications();
  }, [communicationApi, interrogatory, invalidateCommunications]);

  // Helper to optimistically update the cache
  const optimisticallyUpdateTask = useCallback(
    (task: ViewCommunicationItem, type: "associate" | "disassociate") => {
      if (!interrogatory?.id) return;
      const currentInterrogatoryId = interrogatory.id.toString();

      // Cancel any in-flight refetches so they don't overwrite our optimistic update
      queryClient.cancelQueries({ queryKey: communicationsQueryKey });

      queryClient.setQueryData<ViewCommunication[]>(
        communicationsQueryKey,
        (old: ViewCommunication[] | undefined) => {
          if (!old) return old;
          return old.map((comm: ViewCommunication) => ({
            ...comm,
            externalCommunicationItems: comm.externalCommunicationItems?.map(
              (item: ViewCommunicationItem) => {
                if (item.id === task.id) {
                  const existingIds =
                    item.responseInfo?.associatedObjectIds ?? [];
                  return {
                    ...item,
                    responseInfo: {
                      ...item.responseInfo,
                      associatedObjectIds:
                        type === "associate"
                          ? [...existingIds, currentInterrogatoryId]
                          : existingIds.filter(
                              (id: string) => id !== currentInterrogatoryId,
                            ),
                    },
                  };
                }
                return item;
              },
            ),
          }));
        },
      );
    },
    [queryClient, communicationsQueryKey, interrogatory],
  );

  // Smart enqueue that cancels out opposite operations on the same task
  const enqueueOperation = useCallback(
    (newOp: QueuedOperation) => {
      const oppositeType =
        newOp.type === "associate" ? "disassociate" : "associate";
      const existingIndex = operationQueueRef.current.findIndex(
        (op) => op.task.id === newOp.task.id && op.type === oppositeType,
      );

      if (existingIndex !== -1) {
        // Found opposite operation - they cancel out, remove it and don't add new one
        operationQueueRef.current.splice(existingIndex, 1);
        // UI is already correct from optimistic updates
      } else {
        // No opposite found - add to queue normally
        operationQueueRef.current.push(newOp);
        processQueue();
      }
    },
    [processQueue],
  );

  /* CREATE COMMUNICATION - returns existing ID or creates new one */
  const createCommunication = useCallback(async (): Promise<
    string | undefined
  > => {
    // Return existing if available
    if (activeCommunication?.id) {
      return activeCommunication.id;
    }

    // Can't create without case data
    if (!doc?.caseId || !caseQuery.data?.shortTitle) {
      console.error("Case data not available");
      return undefined;
    }

    try {
      const newCommunication =
        await communicationApi.communicationCreateClientCommunication({
          createClientCommunication: {
            objectType: ObjectTypeEnum.Document,
            objectId: documentId,
            caseId: doc.caseId,
            communicationType: CommunicationType.Review,
            status: CommunicationStatus.Pending,
            communicationInfo: {
              title: requestName,
              caseTitle: caseQuery.data.title || caseQuery.data.shortTitle,
            },
          },
        });
      invalidateCommunications();
      return newCommunication.id;
    } catch (error) {
      console.error("Failed to create communication:", error);
      return undefined;
    }
  }, [
    activeCommunication,
    doc,
    caseQuery.data,
    documentId,
    requestName,
    communicationApi,
    invalidateCommunications,
  ]);

  /* HANDLERS */
  const handleOpenCollectFromClientModal = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get(SEARCH_PARAMS.COLLECT_FROM_CLIENT) === "true") {
      return;
    }

    searchParams.set(SEARCH_PARAMS.COLLECT_FROM_CLIENT, "true");
    history.push({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  }, [location, history]);

  return {
    // Modal state
    isLoading,
    isProcessingQueue,
    handleOpenCollectFromClientModal,

    // Shared data
    doc,
    interrogatory,
    interrogatoryText,
    requestName,
    number,
    activeCommunication,
    hasAnySentCommunication,

    // Communication creation
    createCommunication,

    // Operation queue system (for associate/disassociate)
    enqueueOperation,
    optimisticallyUpdateTask,
  };
}
